import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { AdminBrandDisputeDetail, adminDecideDispute } from '../../api/payment/brand-deal.api';
import {
  DECISION_REASON_LIMITS,
  DISPUTE_PERCENT_OPTIONS,
  disputeSplitPreview,
  shouldOfferSanction,
  validateDecisionReason,
} from '../../utils/brand-dispute';

// Hộp quyết kháng cáo (admin, spec 2026-10-10-brand-order-rules §4): chọn tỉ lệ creator nhận 0–100% bước 10% (không chọn sẵn,
// ruling R10), xem trước tiền hai bên theo đúng công thức payment, lý do 10–1.000 ký tự, mã Authenticator. Khai ở cấp module
// (landmine remount). Quyết xong gọi `onDecided(tỉ lệ)`; cha đóng hộp và mở bước xử phạt khi từ 10% trở lên.

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

export interface AdminDisputeDecideDialogProps {
  detail: AdminBrandDisputeDetail;
  onClose: () => void;
  onDecided: (creatorPercent: number) => void;
}

const AdminDisputeDecideDialog: React.FC<AdminDisputeDecideDialogProps> = ({ detail, onClose, onDecided }) => {
  const [percent, setPercent] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);

  const preview =
    percent == null
      ? null
      : disputeSplitPreview({
          priceVnd: detail.priceVnd ?? 0,
          feePercent: detail.feePercent ?? 0,
          ordererKind: detail.ordererKind,
          vndPerCa: detail.vndPerCa ?? 1,
          percent,
        });
  const reasonError = validateDecisionReason(reason);
  const canSubmit = percent != null && !reasonError && totp.length === 6 && !busy;

  const submit = async () => {
    if (percent == null) {
      toast.error('Hãy chọn kết quả.');
      return;
    }
    if (reasonError) {
      toast.error(reasonError);
      return;
    }
    if (totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số.');
      return;
    }
    setBusy(true);
    try {
      await adminDecideDispute(detail.dispute.id, totp, percent, reason.trim());
    } catch (e) {
      toast.error(errorMessage(e, 'Mã Authenticator không hợp lệ hoặc xử lý thất bại.'));
      setBusy(false);
      return;
    }
    onDecided(percent);
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-[480px] rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-800"
        onClick={(e) => e.stopPropagation()}
      >
        <h4 className="mb-4 text-lg font-semibold text-slate-800 dark:text-white">Quyết kháng cáo</h4>

        <label htmlFor="dispute-creator-percent" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
          Creator nhận <span className="text-red-500">*</span>
        </label>
        <select
          id="dispute-creator-percent"
          value={percent ?? ''}
          onChange={(e) => setPercent(e.target.value === '' ? null : Number(e.target.value))}
          className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#0057FF] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
        >
          <option value="">Chọn tỉ lệ</option>
          {DISPUTE_PERCENT_OPTIONS.map((p) => (
            <option key={p} value={p}>
              {p}%
            </option>
          ))}
        </select>

        {preview && (
          <div className="mb-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600 dark:bg-slate-900 dark:text-slate-300">
            <p>{preview.creatorLabel}</p>
            <p>{preview.refundLabel}</p>
          </div>
        )}
        {percent != null && shouldOfferSanction(percent) && (
          <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
            Người đặt đơn không tự bị khoá. Xác nhận xong, bạn có thể xử phạt người đặt đơn ngay ở bước tiếp theo hoặc bỏ qua.
          </p>
        )}

        <label htmlFor="dispute-decision-reason" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
          Lý do ({DECISION_REASON_LIMITS.minLength}–{DECISION_REASON_LIMITS.maxLength} ký tự) <span className="text-red-500">*</span>
        </label>
        <textarea
          id="dispute-decision-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={DECISION_REASON_LIMITS.maxLength}
          rows={4}
          placeholder="VD: bài nộp khớp phần lớn brief, chỉ thiếu cảnh mở hộp."
          className="mb-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-[#0057FF] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
        />
        <div className="mb-4 text-right text-xs text-slate-400">
          {reason.trim().length}/{DECISION_REASON_LIMITS.maxLength}
        </div>

        <label htmlFor="dispute-decision-totp" className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
          Mã Authenticator (6 số) <span className="text-red-500">*</span>
        </label>
        <input
          id="dispute-decision-totp"
          value={totp}
          onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          inputMode="numeric"
          className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-center text-lg tracking-[0.3em] outline-none focus:border-[#0057FF] dark:border-slate-600 dark:bg-slate-900 dark:text-white"
        />

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-10 min-w-[90px] rounded-md border border-gray-300 px-4 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Huỷ
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={submit}
            className="h-10 min-w-[130px] rounded-md bg-green-500 px-4 text-sm font-semibold text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Xác nhận
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDisputeDecideDialog;
